import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import componentStyles from './se-tabs.css.lit';

import { MobileController } from '../../controllers/mobile-controller';

export const seTabTypes = [
  'episodes',
  'alerts',
  'blogs',
  'events',
  'articles',
  'news',
  'press',
] as const;

export type SeTabType = typeof seTabTypes[number];

/**
 * The SeTabs component
 * @element se-tabs
 * @slot episodes
 * @slot alerts
 * @slot blogs
 * @slot events
 * @slot articles
 * @slot news
 * @slot press
 */
@customElement('se-tabs')
export class SeTabs extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  mobileController = new MobileController(this);
  resizeObserver: ResizeObserver;

  @queryAssignedElements({ slot: 'episodes' })
  episodesSlot: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'alerts' })
  alertsSlot: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'blogs' })
  blogsSlot: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'events' })
  eventsSlot: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'articles' })
  articlesSlot: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'news' })
  newsSlot: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'press' })
  pressSlot: Array<HTMLElement>;

  /**
   * active tab
   */
  @property({ type: String, reflect: true })
  active: SeTabType;

  @property({ type: Boolean })
  isMobile = false;

  /**
   * Whether the component has passed first loading.
   */
  @property({ type: Boolean })
  loaded = false;

  // provides unique id number to pass to elements.
  seed: string = Math.floor(Math.random() * 10000).toString();

  /**
   * dictionary of titles
   */
  displayTitles = {
    episodes: 'episodes',
    alerts: 'alerts',
    blogs: 'blog posts',
    events: 'events',
    articles: 'articles',
    news: 'news',
    press: 'press releases',
  };

  /**
   * Array of tabs with slotted content.
   */
  tabsPresent = [];

  render(): TemplateResult {
    return html`
      <section class="se-tabs">
        ${this.toggleControlTemplate()}
        <div class="se-tabs--cards" aria-atomic="true" aria-live="polite">
          <div class="se-tabs--panel" id="episodes-panel">
            <slot name="episodes"></slot>
          </div>
          <div class="se-tabs--panel" id="alerts-panel">
            <slot name="alerts"></slot>
          </div>
          <div class="se-tabs--panel" id="blogs-panel">
            <slot name="blogs"></slot>
          </div>
          <div class="se-tabs--panel" id="events-panel">
            <slot name="events"></slot>
          </div>
          <div class="se-tabs--panel" id="articles-panel">
            <slot name="articles"></slot>
          </div>
          <div class="se-tabs--panel" id="news-panel">
            <slot name="news"></slot>
          </div>
          <div class="se-tabs--panel" id="press-panel">
            <slot name="press"></slot>
          </div>
        </div>
      </section>
    `;
  }

  firstUpdated() {
    this.setTabsPresent();
    this.loaded = true;
    this.isMobile = this.mobileController.isMobile;
    this.setActive();
    this.resizeObserver.observe(this);
  }

  updated() {
    this.toggleActiveClasses();
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => {
      this.setIsMobile();
    });
  }

  setIsMobile() {
    if (this.isMobile !== this.mobileController.isMobile) {
      this.isMobile = this.mobileController.isMobile;
    }
  }

  /**
   *
   * @param slot
   * @returns boolean: if slot has content.
   */
  checkSlot(slot: string) {
    // @ts-expect-error - because ts
    return this[slot].length > 0;
  }

  /**
   * calls checkSLot on all slots and updates this.tabsPresent accordingly.
   */
  setTabsPresent() {
    seTabTypes.forEach(tab => {
      const slotName = `${tab}Slot`;
      if (this.checkSlot(slotName)) {
        // @ts-expect-error - linter keeps changing this.tabsPresent from Array() to []
        this.tabsPresent.push(tab);
      }
    });
  }

  setActive(id?: SeTabType) {
    if (!id) {
      this.setActive(this.tabsPresent[0]);
    } else {
      this.active = id;
    }
  }

  /**
   * Determines which tabs template to render
   * @returns Template
   */
  toggleControlTemplate() {
    return this.mobileController.isMobile
      ? this.mobileTemplate()
      : this.desktopTemplate();
  }

  desktopTemplate() {
    const tabs = this.loaded ? this.tabsPresent : seTabTypes;

    return html`<div
      role="tablist"
      id="tablist-${this.seed}"
      class="se-tabs--tablist-desktop"
    >
      ${tabs.map(tab => {
        return this.generateControl(tab);
      })}
    </div>`;
  }

  mobileTemplate() {
    const tabs = this.loaded ? this.tabsPresent : seTabTypes;
    const buttonLabel = this.active
      ? this.displayTitles[this.active]
      : this.displayTitles[this.tabsPresent[0] as SeTabType];

    return html` <se-button
        class="se-tabs--button"
        variant="dropdown-alt"
        @click="${this.toggleMobileMenu}"
      >
        ${buttonLabel}</se-button
      >
      <ul class="se-tabs--tablist-mobile closed" id="tablist-${this.seed}">
        ${tabs.map(tab => this.generateControl(tab))}
      </ul>`;
  }

  generateControl(tab: SeTabType) {
    if (!this.isMobile) {
      return html`<button
        class="se-tabs--button"
        role="tab"
        type="button"
        id="${tab}-tab"
        aria-controls="${tab}-panel"
        @click=${this.clickHandler}
        aria-selected="false"
      >
        ${this.displayTitles[tab].toUpperCase()}
      </button>`;
    } else {
      return html` <li>
        <button
          class="se-tabs--button mobile-button"
          role="tab"
          type="button"
          id="${tab}-tab"
          aria-controls="${tab}-panel"
          @click=${this.clickHandler}
          aria-selected="false"
        >
          ${this.displayTitles[tab]}
        </button>
      </li>`;
    }
  }

  toggleMobileMenu() {
    const menu = this.shadowRoot?.querySelector('.se-tabs--tablist-mobile');
    menu?.classList.toggle('closed');
  }

  toggleActiveClasses() {
    const currentlyActive = this.shadowRoot?.querySelectorAll('.active');
    currentlyActive?.forEach(el => el.classList.remove('active'));
    const tab = this.shadowRoot?.querySelector(`#${this.active}-tab`);
    const panel = this.shadowRoot?.querySelector(`#${this.active}-panel`);
    [tab, panel].forEach(el => el?.classList.add('active'));
  }

  clickHandler(e: Event) {
    const clicked = e.target as HTMLElement;
    if (clicked.id) {
      const verified = clicked.id.split('-')[0] as SeTabType;
      this.active = verified;

      if (this.isMobile) {
        this.toggleMobileMenu();
      }
    }
  }
}
